/**
 * Value-map editor wiring for MappingPhase (column-mapping-ui spec, "Constant
 * and value-map editors"): visibly flags any sampled source value not yet
 * mapped, using the shared `@ooh/contracts` applyValueMap engine — never a
 * local reimplementation — so the flagging agrees with what the API/worker
 * apply at import time.
 */
import { applyValueMap } from '@ooh/contracts';
import {
  getEnumValuesForFieldKey,
  isQueryBackedEnumFieldKey,
} from '../lib/constant-value-enum-options';
import { FormatEnumOptions } from './format-enum-options';
import type { ColumnMappingItem, MappingPhaseLabels } from './mapping-phase';
import type { TransformMode } from './mapping-transforms-mode-toggle';
import {
  ValueMapEditorDialog,
  type ValueMapEditorLabels,
  type ValueMapEntry,
} from './mapping-transforms-value-map-dialog';

export function buildValueMapEditorLabels(labels: MappingPhaseLabels): ValueMapEditorLabels {
  return {
    dialogTitle: labels.valueMapDialogTitle,
    columnLabelPrefix: labels.patternColumnLabelPrefix,
    dialogInfoLabel: labels.transformDialogInfoLabel,
    dialogInfoText: labels.transformDialogInfoText,
    fromColumnLabel: labels.valueMapFromColumnLabel,
    toColumnLabel: labels.valueMapToColumnLabel,
    unmappedPlaceholder: labels.valueMapUnmappedPlaceholder,
    blockedWarningTemplate: labels.valueMapBlockedWarningTemplate,
    doneButtonLabel: labels.patternDoneButtonLabel,
    transformModePatternLabel: labels.transformModePatternLabel,
    transformModeValueMapLabel: labels.transformModeValueMapLabel,
    modeToggleInfoLabel: labels.modeToggleInfoLabel,
    modeToggleInfoText: labels.modeToggleInfoText,
    valueSearchPlaceholder: labels.constantValueSearchPlaceholder,
    valueNoResultsLabel: labels.fieldNoResults,
    enumOptionsLoadingLabel: labels.enumOptionsLoadingLabel,
    enumOptionsErrorLabel: labels.enumOptionsErrorLabel,
  };
}

function resolveColumnValues(
  sourceColumn: string,
  columnDistinctValues: Record<string, string[]> | undefined,
  columnSamples: Record<string, string[]> | undefined,
): string[] {
  return columnDistinctValues?.[sourceColumn] ?? columnSamples?.[sourceColumn] ?? [];
}

/** Distinct source values for a valueMap column, each flagged `unmapped` when not covered by the current map. */
export function computeValueMapEntries(
  mapping: ColumnMappingItem,
  values: string[],
): ValueMapEntry[] {
  const transform = mapping.transform;
  if (!transform || transform.type !== 'valueMap') return [];
  const distinct = Array.from(
    new Set(values.map((value) => value.trim()).filter((value) => value !== '')),
  );
  return distinct.map((from) => {
    const result = applyValueMap(from, transform);
    // A dictionary entry mapping to an empty/blank target does not count as resolved —
    // mirrors the spec intent that every sampled value must be mapped to a non-empty
    // target before import (column-mapping-ui spec: "Constant and value-map editors").
    // The typed value is still surfaced as `to` so the operator's in-progress input isn't
    // discarded, but `unmapped` stays true so it keeps blocking Next.
    const isResolved = result.matched && result.value.trim() !== '';
    return isResolved
      ? { from, to: result.value, unmapped: false }
      : { from, to: result.matched ? result.value : '', unmapped: true };
  });
}

/** True when any valueMap-transformed column has a distinct source value not yet covered by its map — blocks Next. */
export function hasUnmappedValueMapEntries(
  mappings: ColumnMappingItem[],
  columnDistinctValues: Record<string, string[]> | undefined,
  columnSamples?: Record<string, string[]> | undefined,
): boolean {
  return mappings.some((m) =>
    computeValueMapEntries(
      m,
      resolveColumnValues(m.sourceColumn, columnDistinctValues, columnSamples),
    ).some((e) => e.unmapped),
  );
}

/** Records a from -> to mapping entry, resolving one previously-unmapped sampled value. */
export function applyValueMapEntryChange(
  mappings: ColumnMappingItem[],
  rowId: string,
  from: string,
  to: string,
): ColumnMappingItem[] {
  return mappings.map((m) => {
    if (m.id !== rowId || m.transform?.type !== 'valueMap') return m;
    return { ...m, transform: { ...m.transform, map: { ...m.transform.map, [from]: to } } };
  });
}

export interface MappingValueMapEditorSlotProps {
  editingMapping: ColumnMappingItem | null;
  columnSamples: Record<string, string[]> | undefined;
  columnDistinctValues?: Record<string, string[]> | undefined;
  labels: ValueMapEditorLabels;
  onEntryChange: (id: string, from: string, to: string) => void;
  /** Switches the column's transform mode back to pattern. */
  onTransformModeChange: (id: string, mode: TransformMode) => void;
  onClose: () => void;
}

interface QueryBackedValueMapEditorProps {
  sourceColumnLabel: string;
  entries: ValueMapEntry[];
  labels: ValueMapEditorLabels;
  onEntryChange: (from: string, to: string) => void;
  onModeChange: (mode: TransformMode) => void;
  onClose: () => void;
}

/**
 * Renders ValueMapEditorDialog for a query-backed enum target field (currently only
 * 'format') — isolated into its own component for the same reason as
 * QueryBackedConstantEditor in mapping-phase-constant-editor.tsx: the format dictionary is
 * only requested while a 'format' column is being edited, so every other mappedTo field's
 * value-map editor stays on the synchronous static-enum path below.
 */
function QueryBackedValueMapEditor({
  sourceColumnLabel,
  entries,
  labels,
  onEntryChange,
  onModeChange,
  onClose,
}: QueryBackedValueMapEditorProps) {
  return (
    <FormatEnumOptions>
      {({ options, isLoading, isError }) => (
        <ValueMapEditorDialog
          sourceColumnLabel={sourceColumnLabel}
          entries={entries}
          labels={labels}
          enumOptions={options}
          enumOptionsLoading={isLoading}
          enumOptionsError={isError}
          onEntryChange={onEntryChange}
          onModeChange={onModeChange}
          onClose={onClose}
        />
      )}
    </FormatEnumOptions>
  );
}

/** Renders ValueMapEditorDialog for the column currently being edited, or nothing (mirrors
 * MappingPatternEditorSlot from mapping-phase-transform-editor.tsx). */
export function MappingValueMapEditorSlot({
  editingMapping,
  columnSamples,
  columnDistinctValues,
  labels,
  onEntryChange,
  onTransformModeChange,
  onClose,
}: MappingValueMapEditorSlotProps) {
  if (editingMapping == null || editingMapping.transform?.type !== 'valueMap') return null;
  const values = resolveColumnValues(
    editingMapping.sourceColumn,
    columnDistinctValues,
    columnSamples,
  );
  const entries = computeValueMapEntries(editingMapping, values);
  const mappedTo = editingMapping.mappedTo;

  if (mappedTo != null && isQueryBackedEnumFieldKey(mappedTo)) {
    return (
      <QueryBackedValueMapEditor
        sourceColumnLabel={editingMapping.sourceColumn}
        entries={entries}
        labels={labels}
        onEntryChange={(from, to) => onEntryChange(editingMapping.id, from, to)}
        onModeChange={(mode) => onTransformModeChange(editingMapping.id, mode)}
        onClose={onClose}
      />
    );
  }

  const enumOptions = mappedTo != null ? getEnumValuesForFieldKey(mappedTo) : undefined;
  return (
    <ValueMapEditorDialog
      sourceColumnLabel={editingMapping.sourceColumn}
      entries={entries}
      labels={labels}
      enumOptions={enumOptions}
      onEntryChange={(from, to) => onEntryChange(editingMapping.id, from, to)}
      onModeChange={(mode) => onTransformModeChange(editingMapping.id, mode)}
      onClose={onClose}
    />
  );
}
